// Figma: "Toast" component (node 18050:30), used for transient feedback — server errors,
// rate limiting, Google SSO failure (login-server-error / login-email-rate-limited /
// login-google-sso-error), and the "link sent successfully" confirmation on resend
// (login-resend-confirmation). Bottom-right, auto-dismisses, closable, with a progress bar.
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Box, IconButton, Stack, Typography} from '@mui/material';
import {tokens} from '../../../themes/ThemeCustomization';
import toastCloseIcon from '../../../assets/auth/toast-close-icon.svg';

const AUTO_DISMISS_MS = 5000;

const AuthToast = ({toast, onClose}) => {
    useEffect(() => {
        if (!toast) return undefined;
        const timer = setTimeout(onClose, AUTO_DISMISS_MS);
        return () => clearTimeout(timer);
    }, [toast, onClose]);

    if (!toast) return null;

    const progressColor = toast.variant === 'success' ? tokens.color.success500 : tokens.color.error500;

    return (
        <Box
            sx={{
                position: 'fixed',
                right: 16,
                bottom: 16,
                width: '384px',
                maxWidth: 'calc(100vw - 32px)',
                backgroundColor: tokens.color.white,
                border: `1px solid ${tokens.color.neutral200}`,
                borderRadius: tokens.radius.toast,
                boxShadow: tokens.shadow.toast,
                overflow: 'hidden',
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                zIndex: 1400
            }}
        >
            <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
                <Typography variant="body2" fontWeight={500}>
                    {toast.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {toast.description}
                </Typography>
            </Stack>
            <IconButton size="small" onClick={onClose} sx={{alignSelf: 'flex-start', p: 0.5}}>
                <Box component="img" src={toastCloseIcon} alt="Dismiss" sx={{width: 12, height: 12}} />
            </IconButton>
            <Box
                key={toast.title + toast.description}
                sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: '4px',
                    backgroundColor: progressColor,
                    animation: `auth-toast-shrink ${AUTO_DISMISS_MS}ms linear forwards`,
                    transformOrigin: 'left',
                    '@keyframes auth-toast-shrink': {
                        from: {transform: 'scaleX(1)'},
                        to: {transform: 'scaleX(0)'}
                    }
                }}
            />
        </Box>
    );
};

AuthToast.propTypes = {
    toast: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        variant: PropTypes.oneOf(['error', 'success'])
    }),
    onClose: PropTypes.func.isRequired
};

export default AuthToast;
